import { Alert } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { SectionTabs, withCount } from '~/components/common/SectionTabs';
import { ElevatorInvoicesTable } from '~/components/invoices/detailPage/related/ElevatorInvoicesTable';
import { ElevatorTicketsTable } from '~/components/invoices/detailPage/related/ElevatorTicketsTable';
import { buildCarColumns } from '~/components/portfolio/propertyCarColumns';
import { LiftAITable } from '~/components/table';
import type { TaskDetail } from '~/data/generated/model';
import useInvoices from '~/data/hooks/useInvoices';
import { useTickets } from '~/data/hooks/useTickets';
import type { CarInProperty } from '~/data/schemas/car';
import { InvoiceKind } from '~/data/schemas/invoice';
import { useDetailTableHeight } from '~/hooks/useDetailTableHeight';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { usePropertyCarsKpis } from '~/hooks/usePropertyCarsKpis';
import { buildUrlForElevator } from '~/utils/urlBuilder';

const NO_ACTIONS: never[] = [];

/** The task's elevators, or every elevator on the property when the task names none. */
export function useTaskCars(task: TaskDetail) {
  const { cars = [], loading } = usePropertyCarsKpis(task.propertyId);
  const propertyWide = task.cars.length === 0;
  const rows = useMemo(() => {
    if (propertyWide) return cars.filter((car) => car.id != null);
    const ids = new Set(task.cars.map((car) => car.id));
    return cars.filter((car) => car.id != null && ids.has(car.id));
  }, [cars, propertyWide, task.cars]);
  const carIds = useMemo(
    () =>
      propertyWide
        ? rows.map((car) => car.id).filter((id): id is string => Boolean(id))
        : task.cars.map((car) => car.id),
    [propertyWide, rows, task.cars],
  );
  return { rows, carIds, propertyWide, loading };
}

function ElevatorList({
  task,
  rows,
  loading,
}: {
  task: TaskDetail;
  rows: CarInProperty[];
  loading: boolean;
}) {
  const tableHeight = useDetailTableHeight();
  const apiRef = useGridApiRef();
  const terms = useLocaleTerms();
  const [searchParams] = useSearchParams();

  const carDetailPath = useCallback(
    (carId: string) => buildUrlForElevator(carId, searchParams),
    [searchParams],
  );
  const columns = useMemo(
    () =>
      buildCarColumns({
        propertyId: task.propertyId,
        canEdit: false,
        getActionsEl: () => NO_ACTIONS,
        terms,
        carDetailPath,
      }),
    [task.propertyId, terms, carDetailPath],
  );
  const rowHref = useCallback(
    (car: CarInProperty) => (car.id ? carDetailPath(car.id) : undefined),
    [carDetailPath],
  );

  return (
    <LiftAITable<CarInProperty>
      height={tableHeight}
      apiRef={apiRef}
      rows={rows}
      columns={columns}
      rowCount={rows.length}
      loading={loading}
      tableHeaderTitle={terms.vehicles}
      rowHref={rowHref}
      getRowId={(row) => row.id ?? row.ahjId}
      initialState={{
        columns: {
          columnVisibilityModel: {
            id: false,
            capacityLbs: false,
            controllerModel: false,
            driveType: false,
            lastSspiTest: false,
            numEntrances: false,
            numLandings: false,
            oem: false,
            speedFpm: false,
          },
        },
      }}
    />
  );
}

export function TaskElevatorsTab({ task }: { task: TaskDetail }) {
  const terms = useLocaleTerms();
  const { rows, carIds, propertyWide, loading } = useTaskCars(task);
  const countable = carIds.length > 0;
  const { numTickets } = useTickets({ cars: carIds, limit: 1 }, countable);
  const { metadata: invoiceMetadata } = useInvoices(
    { cars: carIds, limit: '1', kind: [InvoiceKind.Invoice] },
    countable,
  );
  const { metadata: proposalMetadata } = useInvoices(
    { cars: carIds, limit: '1', kind: [InvoiceKind.Proposal] },
    countable,
  );
  const { metadata: creditMetadata } = useInvoices(
    { cars: carIds, limit: '1', kind: [InvoiceKind.Credit] },
    countable,
  );
  const noCars = `This property has no ${terms.vehicles.toLowerCase()} to show.`;

  if (!loading && rows.length === 0 && propertyWide) {
    return <Alert severity="info">{noCars}</Alert>;
  }

  return (
    <SectionTabs
      ariaLabel={`${terms.vehicles} views`}
      helperText={
        propertyWide
          ? `Property-wide task: records for every ${terms.vehicle.toLowerCase()} on the property`
          : `Records for the ${terms.vehicles.toLowerCase()} on this task`
      }
      tabs={[
        {
          value: 'cars',
          label: withCount(terms.vehicles, loading ? undefined : rows.length),
          render: () => <ElevatorList task={task} rows={rows} loading={loading} />,
        },
        {
          value: 'tickets',
          label: withCount('Tickets', countable ? numTickets : 0),
          render: () => (
            <ElevatorTicketsTable
              carIds={carIds}
              previewList="elevator_tickets"
              emptyMessage={noCars}
            />
          ),
        },
        {
          value: 'invoices',
          label: withCount('Invoices', countable ? invoiceMetadata?.numInvoices : 0),
          render: () => (
            <ElevatorInvoicesTable
              carIds={carIds}
              kind={InvoiceKind.Invoice}
              emptyMessage={noCars}
            />
          ),
        },
        {
          value: 'proposals',
          label: withCount('Proposals', countable ? proposalMetadata?.numInvoices : 0),
          render: () => (
            <ElevatorInvoicesTable
              carIds={carIds}
              kind={InvoiceKind.Proposal}
              emptyMessage={noCars}
            />
          ),
        },
        {
          value: 'credits',
          label: withCount('Credits', countable ? creditMetadata?.numInvoices : 0),
          render: () => (
            <ElevatorInvoicesTable
              carIds={carIds}
              kind={InvoiceKind.Credit}
              emptyMessage={noCars}
            />
          ),
        },
      ]}
    />
  );
}
